import { ApiError, discountRejected, needsRequote, packingKayuRejected } from "@/lib/api";
import { googleAdsPurchase, googleEvent } from "@/lib/google-tag";
import { trackPageView as pixelPageView, trackPixel } from "@/lib/meta-pixel";
import { loadMixpanel, trackMixpanel } from "@/lib/mixpanel";

/**
 * The shopping funnel, reported to every ad platform at once (DEL-435).
 *
 * Pages call these four, not fbq or gtag. Each one maps to the platforms'
 * standard events, so campaigns can optimise on them:
 *
 *   view item       ViewContent       view_item
 *   add to cart     AddToCart         add_to_cart
 *   order placed    InitiateCheckout  begin_checkout
 *   purchase        Purchase          purchase (+ the Ads conversion)
 *
 * "Begin checkout" for the ad platforms is the order placed, the buyer's
 * details submitted and on their way to /bayar, not the checkout form opening:
 * Kanata's agency bids on it as a serious buyer (2026-10-29).
 *
 * All of it is silent off the live storefront. Nothing here decides
 * attribution: the report reads our own orders (DEL-433). These events are for
 * the platforms' bidding.
 *
 * Each one also goes to Mixpanel (DEL-442) under its own snake_case name,
 * product_viewed, product_added_to_cart and checkout_started, plus
 * order_placed, which the ad platforms have no event for. Mixpanel's
 * order_paid is the server's (DEL-444), not this file's. Mixpanel also
 * reports from staging, tagged as such; see lib/mixpanel.ts.
 */

/** What one product line reports: the variant's code, its name and price. */
export interface TrackedItem {
  id: string;
  name: string;
  priceIdr: number;
  qty: number;
}

/** What Mixpanel gets about one product line. */
const mixpanelItem = (i: TrackedItem) => ({ sku_code: i.id, product_name: i.name, price_idr: i.priceIdr });

const items = (list: TrackedItem[]) =>
  list.map((i) => ({ item_id: i.id, item_name: i.name, price: i.priceIdr, quantity: i.qty }));
const value = (list: TrackedItem[]) => list.reduce((n, i) => n + i.priceIdr * i.qty, 0);

export function trackPageView(path: string): void {
  // Mixpanel counts page views itself (autocapture), query strings included:
  // loading it is enough, and loading it here starts the session replay on
  // the first page.
  void loadMixpanel();
  pixelPageView();
  googleEvent("page_view", { page_path: path, page_location: window.location.href });
}

export function trackViewItem(item: TrackedItem, productSlug: string): void {
  trackMixpanel("product_viewed", { ...mixpanelItem(item), product_slug: productSlug });
  trackPixel("ViewContent", {
    content_ids: [item.id],
    content_name: item.name,
    content_type: "product",
    value: item.priceIdr,
    currency: "IDR",
  });
  googleEvent("view_item", { currency: "IDR", value: item.priceIdr, items: items([item]) });
}

/** `isAddon`: a line added alongside the product, rather than the product itself. */
export function trackAddToCart(item: TrackedItem, isAddon = false): void {
  trackMixpanel("product_added_to_cart", { ...mixpanelItem(item), quantity: item.qty, is_addon: isAddon });
  trackPixel("AddToCart", {
    content_ids: [item.id],
    content_name: item.name,
    content_type: "product",
    contents: [{ id: item.id, quantity: item.qty }],
    value: item.priceIdr * item.qty,
    currency: "IDR",
  });
  googleEvent("add_to_cart", { currency: "IDR", value: item.priceIdr * item.qty, items: items([item]) });
}

/** The checkout form opened. Mixpanel only: the ad platforms' InitiateCheckout waits for the order. */
export function trackBeginCheckout(list: TrackedItem[]): void {
  trackMixpanel("checkout_started", { cart_total_idr: value(list), item_count: list.reduce((n, i) => n + i.qty, 0) });
}

/** An order placed, payment not yet made. VA buyers drop off between this and order_paid. */
export interface PlacedOrder {
  orderNumber: string;
  totalIdr: number;
  paymentMethod: string;
  vaBank?: string;
  courier: string;
  items: TrackedItem[];
  discountCode?: string;
  pasang: boolean;
  /** Placed, but the QRIS it asked for was switched off: sent on to pay another way. */
  qrisUnavailable?: boolean;
}

/**
 * Mixpanel's order_placed, and the ad platforms' begin checkout. The value is
 * the order total, shipping included, the figure Purchase sends; the order
 * number is Meta's event ID, so a second report of the same order is dropped.
 */
export function trackOrderPlaced(o: PlacedOrder): void {
  const itemCount = o.items.reduce((n, i) => n + i.qty, 0);
  trackMixpanel("order_placed", {
    order_number: o.orderNumber,
    order_total_idr: o.totalIdr,
    payment_method: o.paymentMethod.toLowerCase(),
    va_bank: o.vaBank?.toLowerCase(),
    courier: o.courier.toLowerCase(),
    item_count: itemCount,
    discount_code: o.discountCode,
    is_pasang: o.pasang,
    is_qris_unavailable: o.qrisUnavailable,
  });
  trackPixel(
    "InitiateCheckout",
    {
      content_ids: o.items.map((i) => i.id),
      content_type: "product",
      num_items: itemCount,
      value: o.totalIdr,
      currency: "IDR",
    },
    o.orderNumber,
  );
  googleEvent("begin_checkout", { currency: "IDR", value: o.totalIdr, items: items(o.items) });
}

// ─── friction (DEL-443) ─────────────────────────────────────────────────
//
// Where visitors get stuck. Mixpanel only: the ad platforms have no use for
// them. Each names the step and why it failed as a short lowercase code, so a
// report can break a funnel's drop-off down by reason.

/** Why a shipping quote came back empty. `no_couriers`: none serve the address. */
export function trackShippingQuoteFailed(reason: "no_couriers" | "request_failed", province: string, city: string): void {
  trackMixpanel("shipping_quote_failed", { reason, province: province.toLowerCase(), city: city.toLowerCase() });
}

export type CheckoutFailure =
  | "requote"
  | "discount_rejected"
  | "packing_kayu_rejected"
  | "invalid"
  | "server_error"
  | "network";

/** Why the API refused an order, from the error it answered with. */
export function checkoutFailureReason(e: unknown): CheckoutFailure {
  if (needsRequote(e)) return "requote";
  if (discountRejected(e)) return "discount_rejected";
  if (packingKayuRejected(e)) return "packing_kayu_rejected";
  if (!(e instanceof ApiError)) return "network";
  return e.status >= 500 ? "server_error" : "invalid";
}

export function trackOrderSubmitFailed(e: unknown, paymentMethod: string, cartTotalIdr: number): void {
  trackMixpanel("order_submit_failed", {
    reason: checkoutFailureReason(e),
    http_status: e instanceof ApiError ? e.status : undefined,
    payment_method: paymentMethod.toLowerCase(),
    cart_total_idr: cartTotalIdr,
  });
}

export function trackDiscountCodeRejected(code: string): void {
  trackMixpanel("discount_code_rejected", { discount_code: code });
}

/**
 * The payment page ended without the money: the code ran out (`expired`), or
 * the processor said no (`failed`). `minutesOnPage` is how long the buyer had
 * the page open, which tells a slow banking app from a walk-away.
 */
export function trackPaymentUnpaid(
  state: "expired" | "failed",
  orderNumber: string,
  paymentMethod: string | undefined,
  minutesOnPage: number,
): void {
  trackMixpanel(state === "expired" ? "payment_expired" : "payment_failed", {
    order_number: orderNumber,
    payment_method: paymentMethod?.toLowerCase(),
    minutes_on_page: Math.round(minutesOnPage),
  });
}

/** A fresh charge for the same order, after one expired or failed. */
export function trackPaymentRestarted(orderNumber: string, paymentMethod: string | undefined): void {
  trackMixpanel("payment_restarted", { order_number: orderNumber, payment_method: paymentMethod?.toLowerCase() });
}

export interface OptionChoice {
  productSlug: string;
  optionType: string;
  optionValue: string;
  /** The variant the choice landed on, when there is one. */
  skuCode?: string;
  soldOut?: boolean;
  inquiryOnly?: boolean;
}

export function trackOptionSelected(c: OptionChoice): void {
  trackMixpanel("product_option_selected", {
    product_slug: c.productSlug,
    option_type: c.optionType.toLowerCase(),
    option_value: c.optionValue.toLowerCase(),
    sku_code: c.skuCode,
    is_sold_out: c.soldOut,
    is_inquiry_only: c.inquiryOnly,
  });
}

export function trackCartItemRemoved(item: TrackedItem): void {
  trackMixpanel("cart_item_removed", { ...mixpanelItem(item), quantity: item.qty });
}

/** The signup popup's moments. `popupKey` tells one campaign's popup from the next. */
export function trackSignupPopup(
  moment: "shown" | "dismissed" | "link_followed" | "submitted",
  popupKey: string,
): void {
  trackMixpanel(`signup_popup_${moment}`, { popup_key: popupKey });
}

/** Whether a link opens WhatsApp. */
export function isWhatsappUrl(href: string): boolean {
  try {
    const host = new URL(href, "https://kanataindonesia.com").hostname;
    // wa.link is the shortener Kanata's own number is published under.
    return ["wa.me", "wa.link", "api.whatsapp.com", "web.whatsapp.com"].includes(host);
  } catch {
    return false;
  }
}

/**
 * A chat opened on WhatsApp. For a lot of desk buyers this is the conversion,
 * not checkout, so it is counted wherever it happens. `placement` is the link's
 * data-wa-placement ("product_inquiry", "footer", …).
 */
export function trackWhatsappClicked(placement: string, productSlug?: string): void {
  trackMixpanel("whatsapp_clicked", { placement, product_slug: productSlug });
}

/**
 * Every WhatsApp link on the site, through one listener rather than an
 * onClick on each: a link added later is counted without anyone remembering
 * to. Mark the link with data-wa-placement, and data-product-slug when it is
 * about one product. Returns the listener's remover.
 */
export function watchWhatsappClicks(doc: Document = document): () => void {
  const onClick = (e: MouseEvent) => {
    const a = (e.target as Element | null)?.closest?.("a[href]");
    if (!a || !isWhatsappUrl(a.getAttribute("href") ?? "")) return;
    const placed = a.closest("[data-wa-placement]");
    trackWhatsappClicked(
      placed?.getAttribute("data-wa-placement") ?? "other",
      a.closest("[data-product-slug]")?.getAttribute("data-product-slug") ?? undefined,
    );
  };
  // Capture phase: a link that closes its dialog on click is gone by bubbling.
  doc.addEventListener("click", onClick, true);
  return () => doc.removeEventListener("click", onClick, true);
}

const PURCHASE_KEY = "kanata.purchases";

/**
 * Whether this browser has already reported an order, and record that it has
 * now. The payment page can be reopened from the lookup page or a refresh,
 * and each visit must not be a new sale. Meta and Google both deduplicate on
 * the order number as well; this saves them the work.
 */
export function firstReport(orderNumber: string, storage: Pick<Storage, "getItem" | "setItem"> | null): boolean {
  if (!storage) return true;
  try {
    const seen: string[] = JSON.parse(storage.getItem(PURCHASE_KEY) ?? "[]");
    if (seen.includes(orderNumber)) return false;
    storage.setItem(PURCHASE_KEY, JSON.stringify([...seen, orderNumber].slice(-20)));
  } catch {
    // Unreadable storage: report, and let the platforms deduplicate.
  }
  return true;
}

function localStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * The sale, from the payment page once it shows "paid". The order number is
 * Meta's event ID and Google's transaction ID: the server's Conversions API
 * sends the same purchase with the same ID, and Meta keeps one.
 */
export function trackPurchase(orderNumber: string, totalIdr: number): void {
  if (typeof window === "undefined" || !firstReport(orderNumber, localStore())) return;
  // Not to Mixpanel: its order_paid comes from the server, which sees every
  // payment, including the ones made after the tab was closed. Mixpanel can't
  // merge a browser copy with a server copy (it would need the same
  // timestamp), so there is one copy, the server's.
  trackPixel("Purchase", { value: totalIdr, currency: "IDR", order_id: orderNumber }, orderNumber);
  googleEvent("purchase", { transaction_id: orderNumber, value: totalIdr, currency: "IDR" });
  googleAdsPurchase({ value: totalIdr, transactionId: orderNumber });
}
